// As 6 etapas da jornada de consultoria, no formato `JourneyTemplate`.
//
// NOTA: Por que já no formato do contrato: quando o backend passar a servir
// templates por escritório, este arquivo vira apenas o FALLBACK — nenhum
// componente muda, porque já leem a estrutura, não estas constantes.
//
// DE-PARA DA AÇÃO — por passo, dois campos que andam juntos:
//
//   `actionKey`   liga ao registry (utils/journey-step-actions.js), que sabe
//                 QUAL modal abrir ou para onde navegar e com que payload.
//   `actionLabel` é o VERBO que aparece no botão da listagem e no atalho da
//                 timeline: "Preencher IPS", não "Executar". Um botão genérico
//                 obriga o profissional a abrir a linha para descobrir o que
//                 vai acontecer; o verbo deixa a fila escaneável.
//
// `actionKey: null` significa "sem fluxo dedicado na plataforma" — estado de
// primeira classe, não erro. Nesse caso `actionLabel` também é null e a UI
// mostra o passo como não-acionável.
export const JOURNEY_TEMPLATE_DEFAULT = {
  id: 'consultoria-v1',
  officeId: null,
  name: 'Consultoria de investimentos',
  version: 1,
  editable: false,
  stages: [
    {
      id: 'qualificacao',
      label: 'Qualificação',
      shortLabel: 'Qualificação',
      description: 'Perfil, patrimônio e aderência ao serviço de consultoria',
      order: 1,
      kind: 'unica',
      slaDays: 7,
      weight: 1,
      steps: [
        {
          id: 'qualificacao.qualificar',
          label: 'Qualificar o cliente',
          description: 'Confirmar patrimônio e fit com a consultoria',
          order: 1,
          required: true,
          actionKey: 'qualificacao.qualificar',
          actionLabel: 'Qualificar cliente',
        },
      ],
    },
    {
      id: 'r1',
      label: 'R1 — Primeira Reunião',
      shortLabel: 'R1',
      description: 'Diagnóstico inicial, apresentação da consultoria e coleta de objetivos',
      order: 2,
      kind: 'unica',
      slaDays: 10,
      weight: 1,
      steps: [
        {
          id: 'r1.realizar',
          label: 'Realizar a primeira reunião',
          description: 'Registrar o diagnóstico e os objetivos do cliente',
          order: 1,
          required: true,
          actionKey: 'r1.realizar',
          actionLabel: 'Registrar reunião',
        },
      ],
    },
    {
      id: 'r2',
      label: 'R2 — Segunda Reunião',
      shortLabel: 'R2',
      description: 'Apresentação da proposta, plano de investimentos e alinhamento de expectativas',
      order: 3,
      kind: 'unica',
      slaDays: 10,
      weight: 1,
      steps: [
        {
          id: 'r2.apresentar-proposta',
          label: 'Apresentar a proposta',
          description: 'Levar o plano de investimentos ao cliente',
          order: 1,
          required: true,
          actionKey: 'r2.apresentar-proposta',
          actionLabel: 'Abrir simulador',
        },
      ],
    },
    {
      id: 'compliance',
      label: 'Compliance',
      shortLabel: 'Compliance',
      description: 'Formalização cadastral, regulatória e contratual',
      order: 4,
      kind: 'unica',
      slaDays: 15,
      weight: 1,
      steps: [
        {
          id: 'compliance.formulario',
          label: 'Preenchimento do formulário',
          description: 'Cadastro completo enviado pelo cliente',
          order: 1,
          required: true,
          actionKey: 'compliance.formulario',
          actionLabel: 'Abrir cadastro',
        },
        {
          id: 'compliance.ips',
          label: 'Preenchimento do IPS',
          description: 'Política de investimentos do cliente',
          order: 2,
          required: true,
          actionKey: 'compliance.ips',
          actionLabel: 'Preencher IPS',
        },
        {
          id: 'compliance.contrato',
          label: 'Geração de contrato',
          description: 'Kit de documentos e assinatura',
          order: 3,
          required: true,
          actionKey: 'compliance.contrato',
          actionLabel: 'Gerar contrato',
        },
        {
          id: 'compliance.pld',
          label: 'Análise de PLD',
          description: 'Prevenção à lavagem de dinheiro (KYC)',
          order: 4,
          required: true,
          actionKey: 'compliance.pld',
          actionLabel: 'Analisar PLD',
        },
      ],
    },
    {
      id: 'onboarding',
      label: 'Onboarding',
      shortLabel: 'Onboarding',
      description: 'Abertura de conta, transferência de custódia e reunião de boas-vindas',
      order: 5,
      kind: 'unica',
      slaDays: 20,
      weight: 1,
      steps: [
        {
          id: 'onboarding.abertura-conta',
          label: 'Abertura de conta',
          description: 'Conta de investimento do cliente',
          order: 1,
          required: true,
          actionKey: 'onboarding.abertura-conta',
          actionLabel: 'Abrir conta',
        },
        {
          id: 'onboarding.portabilidade',
          label: 'Transferência de custódia',
          description: 'Portabilidade dos ativos',
          order: 2,
          required: true,
          actionKey: 'onboarding.portabilidade',
          actionLabel: 'Transferir custódia',
        },
        {
          id: 'onboarding.kickoff',
          label: 'Reunião de kickoff',
          description: 'Boas-vindas e alinhamento da rotina',
          order: 3,
          required: true,
          actionKey: 'onboarding.kickoff',
          actionLabel: 'Agendar kickoff',
        },
      ],
    },
    {
      // NOTA: Recorrente: NÃO conclui. Fica em aberto enquanto o cliente estiver na
      // carteira — por isso a UI precisa diferenciá-la visualmente de "pendente"
      // (ver JourneyStageBar: hachura + cadeado + ícone de ciclo).
      id: 'relacionamento',
      label: 'Relacionamento',
      shortLabel: 'Relacionamento',
      description: 'Rotina contínua de acompanhamento e revisão da carteira',
      order: 6,
      kind: 'recorrente',
      slaDays: 30,
      weight: 1,
      steps: [
        {
          id: 'relacionamento.mensal',
          label: 'Reunião mensal',
          description: 'Acompanhamento do mês',
          order: 1,
          required: true,
          actionKey: 'relacionamento.mensal',
          actionLabel: 'Agendar reunião',
        },
        {
          id: 'relacionamento.trimestral',
          label: 'Revisão trimestral',
          description: 'Revisão da carteira e do plano',
          order: 2,
          required: true,
          actionKey: 'relacionamento.trimestral',
          actionLabel: 'Agendar revisão',
        },
        {
          id: 'relacionamento.anual',
          label: 'Avaliação anual',
          description: 'Balanço do ano e replanejamento',
          order: 3,
          required: false,
          actionKey: null,
          actionLabel: null,
        },
      ],
    },
  ],
}

export const RECORRENTE = 'recorrente'
